"use client";

import { useEffect, useId, useRef, useState, type SVGProps } from "react";

import {
  PERCEPTRON_MARK_FRAMES,
  PERCEPTRON_MARK_LOOP_MS,
  PERCEPTRON_MARK_VIEWBOX,
  type PerceptronMarkFrame,
} from "./perceptron-mark-loop.frames";

export interface PerceptronMarkLoopProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  /** Loop while true. Turned off, the loop plays out its current cycle and rests on the first frame. */
  playing: boolean;
}

const [VIEW_X, VIEW_Y, VIEW_WIDTH, VIEW_HEIGHT] = PERCEPTRON_MARK_VIEWBOX.split(" ").map(Number) as [
  number,
  number,
  number,
  number,
];

/** The frame showing at `progress` (0–1) through the loop. */
const frameIndexAt = (progress: number): number => {
  const index = PERCEPTRON_MARK_FRAMES.findIndex((frame) => progress >= frame.start && progress < frame.end);
  return index === -1 ? PERCEPTRON_MARK_FRAMES.length - 1 : index;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * The Perceptron mark as a looping animation, in the current text colour. Only the frame on screen is
 * rendered (a few dozen elements, not every frame of the loop), stepped on the display's frame clock, and
 * each instance's mask id is its own, so several can be on the page at once.
 */
export function PerceptronMarkLoop({ playing, ...props }: PerceptronMarkLoopProps) {
  const maskId = `perceptron-mark-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [frameIndex, setFrameIndex] = useState(0);
  const [running, setRunning] = useState(false);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  // Under reduced motion it never starts, and stays on the first frame.
  if (playing && !running && !prefersReducedMotion()) setRunning(true);

  useEffect(() => {
    if (!running) return;
    const start = performance.now();
    // Once asked to stop, the end of the cycle it is in.
    let stopAt: number | null = null;
    let frame = 0;
    const tick = (now: number) => {
      const elapsed = now - start;
      if (playingRef.current) {
        stopAt = null;
      } else {
        stopAt ??= (Math.floor(elapsed / PERCEPTRON_MARK_LOOP_MS) + 1) * PERCEPTRON_MARK_LOOP_MS;
        if (elapsed >= stopAt) {
          setFrameIndex(0);
          setRunning(false);
          return;
        }
      }
      setFrameIndex(frameIndexAt((elapsed % PERCEPTRON_MARK_LOOP_MS) / PERCEPTRON_MARK_LOOP_MS));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running]);

  const current: PerceptronMarkFrame = PERCEPTRON_MARK_FRAMES[frameIndex] ?? PERCEPTRON_MARK_FRAMES[0]!;
  return (
    <svg aria-hidden fill="none" viewBox={PERCEPTRON_MARK_VIEWBOX} xmlns="http://www.w3.org/2000/svg" {...props}>
      <defs>
        <mask height={VIEW_HEIGHT} id={maskId} maskUnits="userSpaceOnUse" width={VIEW_WIDTH} x={VIEW_X} y={VIEW_Y}>
          <g fill="none" strokeLinejoin="round" strokeWidth={current.maskWidth}>
            {current.mask.map(([grey, d], index) => {
              const level = Math.round(grey * 255);
              return <path d={d} key={index} stroke={`rgb(${level},${level},${level})`} />;
            })}
          </g>
        </mask>
      </defs>
      <rect
        fill="currentColor"
        height={VIEW_HEIGHT}
        mask={`url(#${maskId})`}
        width={VIEW_WIDTH}
        x={VIEW_X}
        y={VIEW_Y}
      />
      <path
        d={current.outline}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={current.outlineWidth}
      />
    </svg>
  );
}
